import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  ThemeProvider,
  Typography,
  createTheme,
  useMediaQuery,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import InboxIcon from "@mui/icons-material/Inbox";

import { toast } from "react-toastify";
import { schedulingApi } from "../Components/schedulingApi";
import { GlobalSearchBar } from "../Components/ERPList";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import styles from "./AllEnquiries.module.scss";

const theme = createTheme({
  palette: {
    primary: { main: "#5e72e4" },
    secondary: { main: "#f5365c" },
    background: { default: "#f8f9fe" },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h4: { fontWeight: 600, letterSpacing: "-0.02em" },
    button: { textTransform: "none", fontWeight: 500 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

export default function ClassroomsPage() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(!isMobile);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [globalSearch, setGlobalSearch] = useState("");
  const debouncedSearch = useDebouncedValue(globalSearch, 400);
  const [editOpen, setEditOpen] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editingClassroom, setEditingClassroom] = useState(null);
  const [editForm, setEditForm] = useState({
    room_code: "",
    capacity: 1,
    building: "",
    floor: "",
    equipment: "",
    description: "",
    is_active: true,
  });

  const loadClassrooms = useCallback(async () => {
    setLoading(true);
    try {
      const data = await schedulingApi.listClassrooms({ active_only: "false" });
      if (data?.error) {
        toast.error(data.error);
        setClassrooms([]);
        return;
      }
      setClassrooms(data.classrooms || []);
    } catch (e) {
      toast.error(e?.message || "Failed to load classrooms");
      setClassrooms([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClassrooms();
  }, [loadClassrooms]);

  const searchLower = (debouncedSearch || "").trim().toLowerCase();
  const filteredBySearch = searchLower
    ? classrooms.filter((r) => {
        const code = (r.room_code || "").toLowerCase();
        const building = (r.building || "").toLowerCase();
        const equipment = (r.equipment || "").toLowerCase();
        return code.includes(searchLower) || building.includes(searchLower) || equipment.includes(searchLower) || String(r.id).includes(searchLower);
      })
    : classrooms;

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };
  const getSortedData = () => {
    if (!sortConfig.key) return filteredBySearch;
    return [...filteredBySearch].sort((a, b) => {
      const aVal = a[sortConfig.key] ?? "";
      const bVal = b[sortConfig.key] ?? "";
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  };
  const displayData = getSortedData();

  const handleDelete = async (id, roomCode) => {
    if (!window.confirm(`Deactivate classroom "${roomCode}"?`)) return;
    try {
      const data = await schedulingApi.deleteClassroom(id);
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      toast.success("Classroom deactivated.");
      loadClassrooms();
    } catch (e) {
      toast.error(e?.message || "Failed to deactivate");
    }
  };

  const openEditDialog = (room) => {
    setEditingClassroom(room);
    setEditForm({
      room_code: room.room_code || "",
      capacity: room.capacity ?? 1,
      building: room.building || "",
      floor: room.floor || "",
      equipment: room.equipment || "",
      description: room.description || "",
      is_active: Boolean(room.is_active),
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editingClassroom?.id) return;
    if (!String(editForm.room_code || "").trim()) {
      toast.warning("Room code is required.");
      return;
    }
    const cap = Number(editForm.capacity);
    if (!Number.isInteger(cap) || cap < 1) {
      toast.warning("Capacity must be at least 1.");
      return;
    }
    setSavingEdit(true);
    try {
      const payload = {
        room_code: String(editForm.room_code).trim(),
        capacity: cap,
        building: String(editForm.building || "").trim() || null,
        floor: String(editForm.floor || "").trim() || null,
        equipment: String(editForm.equipment || "").trim() || null,
        description: String(editForm.description || "").trim() || null,
        is_active: Boolean(editForm.is_active),
      };
      const data = await schedulingApi.updateClassroom(editingClassroom.id, payload);
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      toast.success("Classroom updated.");
      setEditOpen(false);
      setEditingClassroom(null);
      loadClassrooms();
    } catch (e) {
      toast.error(e?.message || "Failed to update classroom");
    } finally {
      setSavingEdit(false);
    }
  };

  const resetFilters = () => {
    setGlobalSearch("");
    loadClassrooms();
    toast.info("Filters cleared.");
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Paper elevation={0} className={styles.contentPaper}>
          <Box sx={{ marginBottom: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box>
                    <Typography variant="h4" className={styles.pageTitle}>
                      Classrooms
                    </Typography>
                    <Typography variant="body2" className={styles.pageSubtitle} sx={{ mt: 0.5 }}>
                      Room codes, capacity, building and equipment.
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Box className={styles.header}>
                <GlobalSearchBar value={globalSearch} onChange={setGlobalSearch} placeholder="Search room code, building, equipment…" />
                <Box className={styles.headerActions}>
                  <Button startIcon={<FilterListIcon />} onClick={() => setFilterOpen(!filterOpen)} variant="outlined" size="small">
                    {filterOpen ? "Hide Filters" : "Show Filters"}
                  </Button>
                  <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={resetFilters}>
                    Reset
                  </Button>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate("/schedule/classrooms/create")}>
                    Create Classroom
                  </Button>
                </Box>
              </Box>

              {filterOpen && (
                <Fade in={filterOpen}>
                  <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Button variant="contained" startIcon={<SearchIcon />} onClick={loadClassrooms}>
                        Refresh
                      </Button>
                    </Stack>
                  </Paper>
                </Fade>
              )}

              {loading && (
                <Box className={styles.loadingContainer}>
                  <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
                  <Skeleton variant="rectangular" height={400} />
                </Box>
              )}

              {!loading && displayData.length === 0 && (
                <Box className={styles.emptyState}>
                  <InboxIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No classrooms found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create a classroom to get started.
                  </Typography>
                </Box>
              )}

              {!loading && displayData.length > 0 && (
                <>
                  <Paper variant="outlined" className={styles.tableContainer}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <TableSortLabel active={sortConfig.key === "room_code"} direction={sortConfig.key === "room_code" ? sortConfig.direction : "asc"} onClick={() => requestSort("room_code")}>
                              Room code
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel active={sortConfig.key === "capacity"} direction={sortConfig.key === "capacity" ? sortConfig.direction : "asc"} onClick={() => requestSort("capacity")}>
                              Capacity
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>Building</TableCell>
                          <TableCell>Floor</TableCell>
                          <TableCell>Equipment</TableCell>
                          <TableCell>Active</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displayData.map((r) => (
                          <TableRow key={r.id} hover className={styles.tableRow}>
                            <TableCell>
                              <Typography variant="body2" className={styles.primaryText}>
                                {r.room_code}
                              </Typography>
                            </TableCell>
                            <TableCell>{r.capacity}</TableCell>
                            <TableCell>{r.building || "—"}</TableCell>
                            <TableCell>{r.floor || "—"}</TableCell>
                            <TableCell>{r.equipment || "—"}</TableCell>
                            <TableCell>
                              <Chip label={r.is_active ? "Yes" : "No"} size="small" color={r.is_active ? "success" : "default"} variant="outlined" />
                            </TableCell>
                            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button size="small" variant="outlined" startIcon={<CalendarMonthIcon />} onClick={() => navigate("/schedule", { state: { filterClassroomId: r.id } })}>
                                  View schedule
                                </Button>
                                <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => openEditDialog(r)}>
                                  Edit
                                </Button>
                                <Button size="small" variant="outlined" color="error" onClick={() => handleDelete(r.id, r.room_code)}>
                                  Deactivate
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                  <Box className={styles.pagination}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {displayData.length} of {displayData.length} results
                    </Typography>
                  </Box>
                </>
              )}
            </Paper>
        <Dialog open={editOpen} onClose={() => !savingEdit && setEditOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Classroom</DialogTitle>
          <DialogContent sx={{ mt: 1 }}>
            <Stack spacing={2}>
              <TextField
                label="Room code"
                value={editForm.room_code}
                onChange={(e) => setEditForm((p) => ({ ...p, room_code: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Capacity"
                type="number"
                inputProps={{ min: 1 }}
                value={editForm.capacity}
                onChange={(e) => setEditForm((p) => ({ ...p, capacity: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Building"
                value={editForm.building}
                onChange={(e) => setEditForm((p) => ({ ...p, building: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Floor"
                value={editForm.floor}
                onChange={(e) => setEditForm((p) => ({ ...p, floor: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Equipment"
                value={editForm.equipment}
                onChange={(e) => setEditForm((p) => ({ ...p, equipment: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Description"
                value={editForm.description}
                onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
                multiline
                rows={3}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOpen(false)} disabled={savingEdit}>Cancel</Button>
            <Button variant="contained" onClick={saveEdit} disabled={savingEdit}>
              {savingEdit ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}
